<template>
  <iButton
    :width="getOptionValue(LayoutWidth)"
    :variant="resolvedStateButton.variant"
    :size="resolvedStateButton.size"
    :icon-left="resolvedStateButton.iconLeft"
    :icon-right="resolvedStateButton.iconRight"
    :icon-color="resolvedStateButton.iconColor"
    :disabled="isActionDisabled"
    :style-overrides="resolvedStateButton.styleOverrides"
    @click="handleClick"
  >
    {{ buttonLabel }}
  </iButton>
</template>

<script>
import { SubscriptionCardElementMixin } from "%/dynamic-component-editor/standard-components/subscription-card/subscription-card-element-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import { SharedButtonComputedStylesMixin }
  from "%/dynamic-component-editor/standard-elements/button-element/shared-button-computed-styles-mixin";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import AnimationEffectsOptions from "%/dynamic-component-editor/bundles/animation-effects-options";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import { useSubscriptionActionPreviewStore } from "%/dynamic-component-editor/stores/subscription-action-preview-store";
import SubscriptionButtonBaseOptions from "%/dynamic-component-editor/bundles/subscription-button-base-options";
import SubscribeButtonOptions from "%/dynamic-component-editor/bundles/subscribe-button-options";
import SelectedButtonOptions from "%/dynamic-component-editor/bundles/selected-button-options";
import ResubscribeButtonOptions from "%/dynamic-component-editor/bundles/resubscribe-button-options";
import {
  BASE_OPTION_CLASSES,
  DELTA_OPTION_CLASSES,
} from "%/dynamic-component-editor/options/subscription-action/subscription-action-options";
import {
  STATE_KEYS,
  resolveActiveStateKey,
  resolveStatefulButton,
} from "%/dynamic-components/render-engine/helpers/subscription-action-button";
import { useGeneralStore } from "%/stores/general-store";
import { useSubscriptionCheckoutStore } from "%/stores/subscription-checkout-store";
import { links } from "%/enums/link-enums";
import LabelTemplate from "%/dynamic-component-editor/options/label-template";
import {
  PLAN_SHEET_INJECTION_KEY,
  describeOtherProviderSubscription,
  formatLabelTemplate,
  readSessionSubscription,
} from "%/dynamic-component-editor/standard-components/plan-sheet/plan-sheet-state";

const MSG_UNRESOLVED_PLAN = "subscribe action could not resolve the plan it belongs to";

const RETURN_QUERY_PARAM = "redirect";
const SUCCESS_PAGE_SLUG = "subscription-success";
const FAILURE_PAGE_SLUG = "subscription-failure";

export default {
  name: "SubscriptionPlanAction",
  elementPanelSettings: {
    displayName: "Subscription Action",
    displayIcon: "button",
    elementGroup: "subscriptionElements",
    elementType: "element",
  },
  mixins: [SubscriptionCardElementMixin, SharedButtonComputedStylesMixin],
  inject: {
    planSheet: { from: PLAN_SHEET_INJECTION_KEY, default: null },
  },
  options: [LayoutWidth, LabelTemplate],
  optionBundles: [
    new SubscriptionButtonBaseOptions(),
    new SubscribeButtonOptions(),
    new SelectedButtonOptions(),
    new ResubscribeButtonOptions(),
    new AppearanceOptions({
      "default.border-radius": "large",
    }, {
      "border-radius": false,
    }),
    AnimationEffectsOptions,
  ],
  data() {
    return {
      LayoutWidth,
    };
  },
  computed: {
    activeStateKey() {
      if (this.editMode) {
        return useSubscriptionActionPreviewStore().previewState;
      }
      const subscriber = typeof window === "undefined" ? null : window.blclient?.data?.subscriberData;
      const currentSubscription = subscriber
        ? { subscription_plan_id: Number(subscriber.subscription_plan_id), status: subscriber.status }
        : null;
      const subscriptionPlanId = this.planSheet ? this.sheetPlanId : this.resolveSubscriptionPlanId();
      return resolveActiveStateKey({ currentSubscription, subscriptionPlanId: Number(subscriptionPlanId) });
    },
    resolvedStateButton() {
      const state = this.activeStateKey;
      const base = {};
      for (const [family, cls] of Object.entries(BASE_OPTION_CLASSES)) {
        base[family] = this.getOptionValue(cls);
      }
      const delta = {};
      for (const [family, cls] of Object.entries(DELTA_OPTION_CLASSES[state])) {
        delta[family] = this.getOptionValue(cls);
      }
      const resolved = resolveStatefulButton({ activeState: state, base, delta });
      const borderRadius = this.getOptionValue(BorderRadius);
      if (borderRadius) {
        resolved.styleOverrides = { ...resolved.styleOverrides, borderRadius };
      }
      return resolved;
    },
    sheetPlanId() {
      if (!this.planSheet) {
        return 0;
      }
      return Number(this.subscriptionEntityId) || this.planSheet.selectedPlanId;
    },
    sheetPlan() {
      return this.planSheet?.plans.find(plan => Number(plan.id) === this.sheetPlanId) ?? null;
    },
    sheetBlockMessage() {
      if (!this.planSheet || this.editMode) {
        return "";
      }
      return describeOtherProviderSubscription(readSessionSubscription(), this.planSheet.provider);
    },
    isSelectedState() {
      return this.activeStateKey === STATE_KEYS.SELECTED;
    },
    isActionDisabled() {
      if (this.editMode) {
        return this.isSelectedState;
      }
      if (!this.planSheet) {
        return this.isSelectedState;
      }
      return !this.sheetPlanId || this.isSelectedState || Boolean(this.sheetBlockMessage);
    },
    buttonLabel() {
      if (!this.planSheet) {
        return this.resolvedStateButton.label;
      }
      if (this.sheetBlockMessage) {
        return this.sheetBlockMessage;
      }
      if (this.activeStateKey !== STATE_KEYS.SUBSCRIBE) {
        return this.resolvedStateButton.label;
      }
      const plan = this.editMode ? this.getExampleEntity() : this.sheetPlan;
      return plan ? formatLabelTemplate(this.getOptionValue(LabelTemplate), plan, this.planSheet.interval) : this.resolvedStateButton.label;
    },
  },
  methods: {
    resolveReturnUrlAfterCheckout() {
      const inbound = new URLSearchParams(window.location.search).get(RETURN_QUERY_PARAM);
      if (!inbound) {
        return window.location.href;
      }
      let resolved = null;
      try {
        resolved = new URL(inbound, window.location.origin);
      } catch (error) {
        return window.location.href;
      }
      if (resolved.origin !== window.location.origin) {
        return window.location.href;
      }
      return resolved.href;
    },
    resolvePublicationId() {
      const fromLegacyShape = Number(this.entity?.publicationId);
      if (Number.isFinite(fromLegacyShape) && fromLegacyShape > 0) {
        return fromLegacyShape;
      }
      const fromRelated = Number(this.entity?.publication?.id);
      if (Number.isFinite(fromRelated) && fromRelated > 0) {
        return fromRelated;
      }
      const fromHost = Number(window.blclient?.data?.themeDestinationId);
      if (Number.isFinite(fromHost) && fromHost > 0) {
        return fromHost;
      }
      return 0;
    },
    handleClick() {
      if (this.editMode || this.isActionDisabled) {
        return;
      }
      if (this.planSheet) {
        this.planSheet.selectPlan(this.sheetPlanId);
      }
      const subscriptionPlanId = this.planSheet ? this.sheetPlanId : this.resolveSubscriptionPlanId();
      const publicationId = this.resolvePublicationId();
      if (!subscriptionPlanId || !publicationId) {
        console.error(MSG_UNRESOLVED_PLAN, { // eslint-disable-line no-console
          subscriptionPlanId,
          publicationId,
          entity: this.entity,
        });
        return;
      }
      const baseUrl = useGeneralStore().getLink(links.web);
      const redirect = encodeURIComponent(this.resolveReturnUrlAfterCheckout());
      useSubscriptionCheckoutStore().startCheckout(
        subscriptionPlanId,
        publicationId,
        `${baseUrl}/${SUCCESS_PAGE_SLUG}?redirect=${redirect}`,
        `${baseUrl}/${FAILURE_PAGE_SLUG}?redirect=${redirect}`,
      );
    },
  },
};
</script>
